import { Task } from "@/types/v2";
import QueueService from "../queue";
import { TasksRoutes } from "./routes";
import { ApiClient } from "@/api/v2/api";
import { adaptTaskResponse } from "./utils";
import { TasksStorageService } from "./storage";
import { convertObjectKeysToCamel } from "@/utils/v2";
import { flattenDetails, withOfflineSupport } from "@/utils/v2";
import { ref } from "vue";
import { GroupTag } from "@/types/v2";
import { setAvailableTags } from "@/stores/v2/tasks/tags";
import {
  buildTaskDependencyWarnings,
  TaskDependencyWarning,
} from "@/utils/v2/tasks/dependencyWarnings";

interface TasksResponse {
  tasks: Task[];
}

interface TaskResponse {
  task: Task;
  hasDependencies?: boolean;
  availableTags?: GroupTag[];
}

export const taskDependencies = ref<boolean>(false);
export const taskDependencyWarnings = ref<TaskDependencyWarning[]>([]);

export const TasksService = {
  getTasksBySiteKey: async (key: string): Promise<Task[]> => {
    const url = TasksRoutes.GET_TASKS_BY_SITE_KEY.replace("{key}", key);
    const response = await ApiClient.get<TasksResponse>(url);

    return convertObjectKeysToCamel(response.data.tasks);
  },

  startTask: withOfflineSupport(
    async(key: string) => {
      const storedTask: Task | null = await TasksStorageService.getActiveTask();
      if (storedTask && storedTask.key === key) {
        taskDependencies.value = false;
        taskDependencyWarnings.value = [];
        return storedTask;
      }
        const url = TasksRoutes.START_TASK_BY_KEY.replace("{key}", key);
        const response = await ApiClient.patch<TaskResponse>(url, null);

        const adaptedTask = adaptTaskResponse(response.data.task);
        taskDependencies.value = response.data.hasDependencies ?? false;
        taskDependencyWarnings.value = taskDependencies.value
          ? buildTaskDependencyWarnings(
            adaptedTask,
            response.data as unknown as Record<string, unknown>,
          )
          : [];

        if (response.data.availableTags) {
          setAvailableTags(response.data.availableTags);
        }

        return adaptedTask;
    },

    async (key: string) => {
      const storedTask: Task | null = await TasksStorageService.getActiveTask();
      if (storedTask && storedTask.key === key) {
        taskDependencies.value = false;
        taskDependencyWarnings.value = [];
        return storedTask;
      }

      return null;
    }
  ),

  finishTask: withOfflineSupport(
    async (task: Task) => {
      const url = TasksRoutes.FINISH_TASK_BY_KEY.replace("{key}", task.key);
      const preparedTask = flattenDetails(task);
      const response = await ApiClient.patch<TaskResponse>(url, {
        task: preparedTask,
      });

      return response.data.task;
    },

    async (task: Task): Promise<Task> => {
      await QueueService.addToQueue(task);
      return task;
    }
  ),

  resetAppToDefault: async (): Promise<void> => {
    // Clear local storage
    localStorage.clear();
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
    }
    
    // Reload the page to reset the app state
    window.location.reload();
  },
};
